// Phase 44.02 — Admin Audit Log globale (black-box di DapNow)
// Visualizza ogni azione admin tracciata in admin_audit_logs:
// rimborsi, reset password, edit ordine/utente, retry queue, restore crediti.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Filter, X, ShieldAlert, ChevronDown, ChevronUp, Search,
  Receipt, KeyRound, UserCog, ListChecks, Repeat2, AlertTriangle, Wallet,
  CreditCard, Repeat, Trash2, LogIn, LogOut, ShieldOff, ShieldX, Lock,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

// Mappa field → meta (etichetta + icona + accent). Default: generico.
const EVENT_META = {
  // Refund
  refund_processed_cash:   { label: "Rimborso CHF",            Icon: Receipt,       cls: "text-amber-800 bg-amber-50 border-amber-200" },
  refund_processed_credit: { label: "Ripristino corsa",        Icon: Repeat,        cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  // Reset password
  password_reset_link_sent: { label: "Reset password inviato", Icon: KeyRound,      cls: "text-violet-700 bg-violet-50 border-violet-200" },
  // Order edit
  delivery_address:        { label: "Indirizzo consegna",      Icon: ListChecks,    cls: "text-blue-700 bg-blue-50 border-blue-200" },
  pickup_address:          { label: "Indirizzo ritiro",        Icon: ListChecks,    cls: "text-blue-700 bg-blue-50 border-blue-200" },
  status:                  { label: "Stato ordine",            Icon: ListChecks,    cls: "text-blue-700 bg-blue-50 border-blue-200" },
  price:                   { label: "Prezzo",                  Icon: Wallet,        cls: "text-amber-800 bg-amber-50 border-amber-200" },
  final_price:             { label: "Prezzo finale",           Icon: Wallet,        cls: "text-amber-800 bg-amber-50 border-amber-200" },
  order_deleted:           { label: "Ordine eliminato",        Icon: Trash2,        cls: "text-red-700 bg-red-50 border-red-200" },
  // User edit
  admin_internal_notes:    { label: "Note interne admin",      Icon: UserCog,       cls: "text-zinc-700 bg-zinc-100 border-zinc-200" },
  stripe_customer_recovered: { label: "Stripe customer recovery", Icon: CreditCard, cls: "text-violet-700 bg-violet-50 border-violet-200" },
  // Retry queue
  retry_run_now:           { label: "Retry tick manuale",      Icon: Repeat2,       cls: "text-blue-700 bg-blue-50 border-blue-200" },
  retry_force:             { label: "Retry forzato",           Icon: Repeat2,       cls: "text-amber-800 bg-amber-50 border-amber-200" },
  retry_cancel:            { label: "Retry annullato",         Icon: Repeat2,       cls: "text-zinc-700 bg-zinc-100 border-zinc-200" },
  // Phase 44.31.1 — Session events
  session_created:                    { label: "Nuova sessione",                          Icon: LogIn,    cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  session_revoked:                    { label: "Sessione revocata",                       Icon: LogOut,   cls: "text-zinc-700 bg-zinc-100 border-zinc-300" },
  all_sessions_revoked:               { label: "Logout globale",                          Icon: ShieldOff, cls: "text-amber-800 bg-amber-50 border-amber-200" },
  password_change_revoked_sessions:   { label: "Sessioni revocate dopo cambio password",  Icon: Lock,     cls: "text-violet-700 bg-violet-50 border-violet-200" },
  admin_revoked_user_session:         { label: "Sessione revocata da admin",              Icon: ShieldX,  cls: "text-red-700 bg-red-50 border-red-200" },
};

// Phase 44.31.1 — Titoli umani completi per le righe scope="session".
// Risolve "admin · su UUID" → "admin ha revocato la sessione di Mariano".
function buildSessionHeadline(row) {
  const actor = row.admin_name || row.admin_email || (row.actor_id === "system" ? "Sistema" : "Utente");
  const targetName = row.target_user_name || row.target_user_email || row.target_id?.slice(0, 8) || "utente";
  const isSelf = row.actor_id === row.target_id;
  switch (row.field || row.action) {
    case "session_created":
      return `${targetName} ha effettuato l'accesso`;
    case "session_revoked":
      return isSelf
        ? `${targetName} ha disconnesso una propria sessione`
        : `${actor} ha disconnesso una sessione`;
    case "all_sessions_revoked":
      return `${targetName} ha effettuato logout globale dagli altri dispositivi`;
    case "password_change_revoked_sessions":
      return `Altre sessioni di ${targetName} revocate dopo cambio password`;
    case "admin_revoked_user_session":
      return `${actor} ha revocato una sessione di ${targetName}`;
    default:
      return `${actor} · evento sessione`;
  }
}

const TARGET_META = {
  order:       { label: "Ordine", cls: "bg-dapnow-blue-soft text-dapnow-blue" },
  user:        { label: "Utente", cls: "bg-violet-50 text-violet-700" },
  retry_queue: { label: "Retry queue", cls: "bg-amber-50 text-amber-800" },
  unknown:     { label: "Altro", cls: "bg-zinc-100 text-zinc-700" },
};

const POLL_MS = 30_000;

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("it-CH", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

function defaultMeta(field) {
  return { label: field || "evento", Icon: ShieldAlert, cls: "text-zinc-700 bg-zinc-100 border-zinc-200" };
}

function EventPill({ field }) {
  const meta = EVENT_META[field] || defaultMeta(field);
  const Icon = meta.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.cls}`}
      data-testid={`audit-event-pill-${field}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

function TargetPill({ type }) {
  const meta = TARGET_META[type] || TARGET_META.unknown;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function JsonBlock({ value, label }) {
  const [open, setOpen] = useState(false);
  if (value === null || value === undefined) return null;
  const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  if (typeof value === "string") {
    return (
      <div className="text-xs">
        <span className="text-dapnow-muted">{label}: </span>
        <span className="font-mono text-dapnow-ink break-all">{text}</span>
      </div>
    );
  }
  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-dapnow-muted hover:text-dapnow-ink"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {label} {open ? "" : "(JSON)"}
      </button>
      {open && (
        <pre className="mt-1 rounded-lg border border-dapnow-line bg-zinc-50 p-2 font-mono text-[10px] text-dapnow-ink whitespace-pre-wrap break-all max-h-48 overflow-auto">
          {text}
        </pre>
      )}
    </div>
  );
}

function AuditRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const isSession = row.scope === "session";
  const hasDiff = (row.old_value !== null && row.old_value !== undefined && row.old_value !== "")
    || (row.new_value !== null && row.new_value !== undefined && row.new_value !== "");
  const actorLabel = row.admin_name || row.admin_email || (row.actor_id === "system" ? "Sistema" : "admin");
  const targetLabel = row.target_user_name || row.target_user_email || row.target_id;
  return (
    <li
      className="p-3 sm:p-4 border-b border-dapnow-line last:border-b-0"
      data-testid={`audit-row-${row.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <EventPill field={row.field || row.action} />
            <TargetPill type={row.target_type} />
            {row.force_override && (
              <span className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 text-red-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                <AlertTriangle className="h-3 w-3" /> Force-override
              </span>
            )}
            <span className="text-[10px] font-mono text-dapnow-muted">{fmtTs(row.created_at)}</span>
          </div>
          <div className="mt-1.5 text-sm">
            {isSession ? (
              <p className="text-dapnow-ink" data-testid={`audit-headline-${row.id}`}>
                {buildSessionHeadline(row)}
              </p>
            ) : (
              <>
                <span className="font-semibold text-dapnow-ink">{actorLabel}</span>
                <span className="text-dapnow-muted"> · su </span>
                <span className="text-dapnow-ink">{targetLabel}</span>
              </>
            )}
          </div>
          {isSession && (row.device_label || row.ip) && (
            <p className="mt-1 text-xs text-dapnow-muted">
              {row.device_label && <span>{row.device_label}</span>}
              {row.device_label && row.ip && <span> · </span>}
              {row.ip && <span className="font-mono">{row.ip}</span>}
            </p>
          )}
          {row.reason && !isSession && (
            <p className="mt-1 text-xs text-dapnow-muted italic line-clamp-1" title={row.reason}>
              "{row.reason}"
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          data-testid={`audit-expand-btn-${row.id}`}
          className="shrink-0 rounded-lg border border-dapnow-line bg-white px-2.5 h-8 text-xs font-semibold hover:border-dapnow-ink transition inline-flex items-center gap-1"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? "Chiudi" : "Dettagli"}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 rounded-xl border border-dapnow-line bg-zinc-50/60 p-3 space-y-3" data-testid={`audit-row-detail-${row.id}`}>
          {isSession || !hasDiff ? (
            // Phase 44.31.1 — Sessione o evento senza diff: mostra "Dettagli evento"
            <div>
              <p className="overline text-[10px] text-dapnow-muted mb-2">Dettagli evento</p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                {row.device_label && (
                  <>
                    <dt className="text-dapnow-muted">Dispositivo</dt>
                    <dd className="text-dapnow-ink">{row.device_label}</dd>
                  </>
                )}
                {row.ip && (
                  <>
                    <dt className="text-dapnow-muted">IP</dt>
                    <dd className="font-mono text-dapnow-ink">{row.ip}</dd>
                  </>
                )}
                {(row.session_id_short || row.session_id) && (
                  <>
                    <dt className="text-dapnow-muted">Sessione</dt>
                    <dd className="font-mono text-dapnow-ink">{row.session_id_short || row.session_id}</dd>
                  </>
                )}
                {row.reason && (
                  <>
                    <dt className="text-dapnow-muted">Motivo</dt>
                    <dd className="text-dapnow-ink">{row.reason}</dd>
                  </>
                )}
                <dt className="text-dapnow-muted">Attore</dt>
                <dd className="text-dapnow-ink">{actorLabel}</dd>
                <dt className="text-dapnow-muted">Bersaglio</dt>
                <dd className="text-dapnow-ink">{targetLabel}</dd>
                <dt className="text-dapnow-muted">Timestamp</dt>
                <dd className="font-mono text-dapnow-ink">{fmtTs(row.created_at)}</dd>
              </dl>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <p className="overline text-[10px] text-dapnow-muted mb-1">Prima</p>
                <JsonBlock label="old_value" value={row.old_value ?? "—"} />
              </div>
              <div>
                <p className="overline text-[10px] text-dapnow-muted mb-1">Dopo</p>
                <JsonBlock label="new_value" value={row.new_value ?? "—"} />
              </div>
              {row.reason && (
                <div className="sm:col-span-2">
                  <p className="overline text-[10px] text-dapnow-muted mb-1">Motivazione</p>
                  <p className="text-xs text-dapnow-ink">{row.reason}</p>
                </div>
              )}
            </div>
          )}
          <div className="text-[11px] text-dapnow-muted border-t border-dapnow-line pt-2">
            <span>audit_id: <span className="font-mono">{row.id}</span></span>
            {row.admin_id && <>{" · "}<span>admin_id: <span className="font-mono">{row.admin_id}</span></span></>}
          </div>
        </div>
      )}
    </li>
  );
}

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [countsByTarget, setCountsByTarget] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    target_type: "",
    field: "",
    q: "",
    since: "",   // YYYY-MM-DD
    until: "",   // YYYY-MM-DD
  });
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const params = useMemo(() => {
    const p = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    if (filters.target_type) p.target_type = filters.target_type;
    if (filters.field) p.field = filters.field;
    if (filters.q) p.q = filters.q;
    if (filters.since) p.since = `${filters.since}T00:00:00`;
    if (filters.until) p.until = `${filters.until}T23:59:59`;
    return p;
  }, [filters, page]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/audit-logs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setCountsByTarget(data.counts_by_target_type || {});
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);
  useEffect(() => {
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [params]);

  const resetFilters = () => {
    setFilters({ target_type: "", field: "", q: "", since: "", until: "" });
    setPage(0);
  };

  const hasFilters = !!(filters.target_type || filters.field || filters.q || filters.since || filters.until);

  return (
    <div className="space-y-5 overflow-x-hidden" data-testid="admin-audit-log-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="overline">Admin · osservabilità</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Audit Log</h1>
          <p className="text-sm text-dapnow-muted mt-1">
            Tracciato completo di ogni azione amministrativa: rimborsi, reset password, edit ordine/utente, retry queue.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-xl border border-dapnow-line bg-white px-3 h-10 text-sm font-semibold hover:border-dapnow-ink transition"
          data-testid="audit-refresh-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Aggiorna
        </button>
      </header>

      {/* Totali rapidi */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="audit-stats">
        <div className="rounded-2xl border border-dapnow-blue/30 bg-dapnow-blue-soft/60 p-4">
          <p className="overline text-[10px] font-bold text-dapnow-muted">Eventi (filtro)</p>
          <p className="font-display text-3xl font-extrabold mt-1" data-testid="audit-stat-total">{total}</p>
        </div>
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
          <p className="overline text-[10px] font-bold text-dapnow-muted">Ordini</p>
          <p className="font-display text-3xl font-extrabold mt-1">{countsByTarget.order ?? 0}</p>
        </div>
        <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4">
          <p className="overline text-[10px] font-bold text-dapnow-muted">Utenti</p>
          <p className="font-display text-3xl font-extrabold mt-1">{countsByTarget.user ?? 0}</p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="overline text-[10px] font-bold text-dapnow-muted">Retry queue</p>
          <p className="font-display text-3xl font-extrabold mt-1">{countsByTarget.retry_queue ?? 0}</p>
        </div>
      </section>

      {/* Filtri */}
      <section className="rounded-2xl border border-dapnow-line bg-white p-4 space-y-3" data-testid="audit-filters">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="overline text-[10px] font-bold text-dapnow-muted inline-flex items-center gap-2">
            <Filter className="h-3.5 w-3.5" /> Filtri
          </p>
          {hasFilters && (
            <button
              type="button"
              onClick={resetFilters}
              data-testid="audit-filters-reset"
              className="text-xs font-semibold text-dapnow-blue hover:underline inline-flex items-center gap-1"
            >
              <X className="h-3 w-3" /> Reset
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <select
            value={filters.target_type}
            onChange={(e) => { setFilters({ ...filters, target_type: e.target.value }); setPage(0); }}
            data-testid="audit-filter-target-type"
            className="h-10 rounded-xl border border-dapnow-line bg-white px-3 text-xs font-semibold"
          >
            <option value="">Tutti i target</option>
            <option value="order">Ordini</option>
            <option value="user">Utenti</option>
            <option value="retry_queue">Retry queue</option>
          </select>
          <select
            value={filters.field}
            onChange={(e) => { setFilters({ ...filters, field: e.target.value }); setPage(0); }}
            data-testid="audit-filter-event"
            className="h-10 rounded-xl border border-dapnow-line bg-white px-3 text-xs font-semibold"
          >
            <option value="">Tutti gli eventi</option>
            <optgroup label="Sessioni">
              <option value="session_created">Nuova sessione</option>
              <option value="session_revoked">Sessione revocata</option>
              <option value="all_sessions_revoked">Logout globale</option>
              <option value="password_change_revoked_sessions">Sessioni revocate (cambio password)</option>
              <option value="admin_revoked_user_session">Sessione revocata da admin</option>
            </optgroup>
            <optgroup label="Rimborsi & pagamenti">
              <option value="refund_processed_cash">Rimborso CHF</option>
              <option value="refund_processed_credit">Ripristino corsa</option>
              <option value="stripe_customer_recovered">Stripe recovery</option>
            </optgroup>
            <optgroup label="Account">
              <option value="password_reset_link_sent">Reset password</option>
            </optgroup>
            <optgroup label="Ordini">
              <option value="delivery_address">Edit indirizzo consegna</option>
              <option value="pickup_address">Edit indirizzo ritiro</option>
              <option value="status">Edit stato ordine</option>
              <option value="price">Edit prezzo</option>
              <option value="order_deleted">Ordine eliminato</option>
            </optgroup>
            <optgroup label="Retry queue">
              <option value="retry_force">Retry forzato</option>
              <option value="retry_cancel">Retry annullato</option>
              <option value="retry_run_now">Retry tick manuale</option>
            </optgroup>
          </select>
          <div className="relative col-span-2 sm:col-span-1">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-dapnow-muted" />
            <input
              type="text"
              value={filters.q}
              onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(0); }}
              placeholder="Cerca admin, ID, motivo…"
              data-testid="audit-filter-q"
              className="w-full h-10 rounded-xl border border-dapnow-line bg-white pl-8 pr-3 text-xs"
            />
          </div>
          <input
            type="date"
            value={filters.since}
            onChange={(e) => { setFilters({ ...filters, since: e.target.value }); setPage(0); }}
            data-testid="audit-filter-since"
            className="h-10 rounded-xl border border-dapnow-line bg-white px-3 text-xs font-semibold"
            aria-label="Dal"
          />
          <input
            type="date"
            value={filters.until}
            onChange={(e) => { setFilters({ ...filters, until: e.target.value }); setPage(0); }}
            data-testid="audit-filter-until"
            className="h-10 rounded-xl border border-dapnow-line bg-white px-3 text-xs font-semibold"
            aria-label="A"
          />
        </div>
      </section>

      {/* Lista */}
      <section className="rounded-2xl border border-dapnow-line bg-white overflow-hidden" data-testid="audit-list">
        {loading ? (
          <div className="p-8 text-center text-dapnow-muted text-sm">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center" data-testid="audit-empty-state">
            <ShieldAlert className="h-8 w-8 mx-auto text-dapnow-muted" />
            <p className="font-display text-base font-bold mt-2">Nessun evento per i filtri attuali.</p>
            <p className="text-xs text-dapnow-muted mt-1">Rimuovi i filtri per vedere tutto l'archivio.</p>
          </div>
        ) : (
          <ul>
            {items.map((row) => <AuditRow key={row.id} row={row} />)}
          </ul>
        )}
      </section>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <nav className="flex items-center justify-between text-xs" data-testid="audit-pagination">
          <span className="text-dapnow-muted">
            Pagina {page + 1} / {Math.ceil(total / PAGE_SIZE)} · {total} eventi totali
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="audit-pagination-prev"
              className="rounded-lg border border-dapnow-line bg-white px-3 h-9 font-semibold hover:border-dapnow-ink transition disabled:opacity-50"
            >
              ← Prec.
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={(page + 1) * PAGE_SIZE >= total}
              data-testid="audit-pagination-next"
              className="rounded-lg border border-dapnow-line bg-white px-3 h-9 font-semibold hover:border-dapnow-ink transition disabled:opacity-50"
            >
              Succ. →
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
